import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, Loader2, XCircle, ShieldCheck } from "lucide-react";

const POLL_INTERVAL_MS = 1000;
const MAX_ATTEMPTS = 25; // ~25s total — front-loaded for fast confirms

const CheckoutSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("polling"); // polling | paid | expired | pending
    const [details, setDetails] = useState(null);
    const attemptsRef = useRef(0);
    const cartClearedRef = useRef(false);
    const { clearCart } = useCart();

    useEffect(() => {
        if (!sessionId) {
            // No session ID — show friendly pending state, not a scary error
            setState("pending");
            return;
        }
        let cancelled = false;

        const poll = async () => {
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                setDetails(r.data);
                if (r.data.payment_status === "paid") {
                    setState("paid");
                    if (!cartClearedRef.current) {
                        clearCart();
                        cartClearedRef.current = true;
                    }
                    return;
                }
                if (r.data.status === "expired") {
                    setState("expired");
                    return;
                }
                attemptsRef.current += 1;
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState("pending");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (err) {
                // Transient network/proxy error — keep polling instead of
                // showing a scary "couldn't verify" message that alarms
                // customers whose payment already went through.
                if (cancelled) return;
                attemptsRef.current += 1;
                if (attemptsRef.current >= MAX_ATTEMPTS) {
                    setState("pending");
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            }
        };

        poll();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div
            data-testid="checkout-success-page"
            className="bg-[#0B0E14] text-white min-h-screen"
        >
            <Header />
            <main className="pt-32 pb-20 min-h-[80vh] flex items-center">
                <div className="max-w-[800px] mx-auto px-6 md:px-12 w-full">
                    <div className="bs-card p-8 md:p-14 text-center">
                        {state === "polling" && (
                            <Polling />
                        )}
                        {state === "paid" && (
                            <Paid details={details} />
                        )}
                        {state === "expired" && (
                            <Failed
                                title="Session Expired"
                                message="Your checkout session expired before payment was completed."
                            />
                        )}
                        {state === "pending" && (
                            <Failed
                                tone="pending"
                                title="Payment Processing"
                                message="Your payment is being processed. You'll receive a confirmation email shortly. If you have any questions, reach us at badgeandshieldcoffee@gmail.com."
                            />
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

const Polling = () => (
    <div data-testid="checkout-status-polling">
        <Loader2 className="w-12 h-12 text-[#ffffff] mx-auto animate-spin mb-6" />
        <div className="bs-eyebrow justify-center">Verifying Payment</div>
        <h1 className="font-anton text-4xl md:text-5xl uppercase tracking-wide mt-4">
            Standing By...
        </h1>
        <p className="font-body text-slate-400 mt-4 max-w-md mx-auto">
            We're confirming your payment with Stripe. This typically takes
            just a few seconds.
        </p>
    </div>
);

const Paid = ({ details }) => (
    <div data-testid="checkout-status-paid">
        <CheckCircle2 className="w-16 h-16 text-[#ffffff] mx-auto mb-6" />
        <div className="bs-eyebrow justify-center">Order Confirmed</div>
        <h1 className="font-anton text-4xl md:text-5xl uppercase tracking-wide mt-4">
            Mission <span className="text-[#ffffff]">Accomplished.</span>
        </h1>
        <p className="font-body text-slate-300 mt-4 max-w-md mx-auto leading-relaxed">
            Thank you for standing with Badge & Shield Coffee Co. Your order
            has been received and is being processed. A confirmation will hit
            your inbox shortly.
        </p>
        {details?.amount_total != null && (
            <div className="mt-8 inline-flex items-center gap-3 px-6 py-3 border border-[#ffffff]">
                <ShieldCheck className="w-5 h-5 text-[#ffffff]" />
                <span className="font-chivo text-xs tracking-[0.22em] uppercase text-slate-300">
                    Total Charged:
                </span>
                <span className="font-anton text-xl text-white">
                    ${(details.amount_total / 100).toFixed(2)}
                </span>
            </div>
        )}
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
                to="/"
                data-testid="back-home-link"
                className="btn-primary inline-flex"
            >
                Return to Base
            </Link>
            <Link
                to="/#products"
                data-testid="shop-more-link"
                className="btn-secondary inline-flex"
            >
                Shop More
            </Link>
        </div>
    </div>
);

const Failed = ({ title, message, tone = "error" }) => {
    const isPending = tone === "pending";
    return (
        <div data-testid="checkout-status-failed">
            {isPending ? (
                <Loader2 className="w-16 h-16 text-[#eab308] mx-auto mb-6 animate-spin" />
            ) : (
                <XCircle className="w-16 h-16 text-[#004466] mx-auto mb-6" />
            )}
            <h1 className="font-anton text-3xl md:text-4xl uppercase tracking-wide">
                {title}
            </h1>
            <p className="font-body text-slate-300 mt-4 max-w-md mx-auto leading-relaxed">
                {message}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                {!isPending && (
                    <Link to="/cart" className="btn-primary inline-flex">
                        Back to Cart
                    </Link>
                )}
                <Link
                    to="/"
                    className={`${isPending ? "btn-primary" : "btn-secondary"} inline-flex`}
                >
                    {isPending ? "Continue Shopping" : "Home"}
                </Link>
            </div>
        </div>
    );
};

export default CheckoutSuccess;
